import type { ColumnsType } from 'antd/es/table'

import { useQuery } from '@tanstack/react-query'
import { Table, Tag, Tooltip } from 'antd'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useDebounce } from 'react-use'
import { useSearchParams } from 'react-router-dom'

import $api from '../../API'
import { EmptyCellValue } from '../../components/CellsGrid/EmptyCellValue/EmptyCellValue'

import { Filters } from './components'
import { initialState, reducer } from './store'
import type { CAEpayCheckRegisterResponse, CAEpayCheckRegisterRow, CAEpayGroup } from './types'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

const baseCellClassName = 'text-xs font-lato'
const baseHeaderCellClassName = 'text-[0.675rem] uppercase font-lato !bg-transparent'

export function CAEpayCheckRegistersPage(): React.ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()

  const [state, dispatch] = useReducer(reducer, initialState)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (!isInitialized) {
      const groupNumber = searchParams.get('group') || undefined
      const dateFrom = searchParams.get('dateFrom') || undefined
      const dateTo = searchParams.get('dateTo') || undefined
      const checkNumber = searchParams.get('checkNumber') || undefined
      const page = parseInt(searchParams.get('page') || '1', 10)
      const pageSize = parseInt(searchParams.get('pageSize') || '50', 10)

      if (groupNumber) dispatch({ type: 'filters/groupNumber/set', payload: groupNumber })
      if (dateFrom) dispatch({ type: 'filters/dateFrom/set', payload: dateFrom })
      if (dateTo) dispatch({ type: 'filters/dateTo/set', payload: dateTo })
      if (checkNumber) dispatch({ type: 'filters/checkNumber/set', payload: checkNumber })
      if (page !== 1) dispatch({ type: 'pagination/page/set', payload: page })
      if (pageSize !== 50) dispatch({ type: 'pagination/pageSize/set', payload: pageSize })

      setIsInitialized(true)
    }
  }, [isInitialized, searchParams])

  const [groupSearchValue, setGroupSearchValue] = useState('')
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('')

  useEffect(() => {
    const params = new URLSearchParams()
    if (state.filters.groupNumber) params.set('group', state.filters.groupNumber)
    if (state.filters.dateFrom) params.set('dateFrom', state.filters.dateFrom)
    if (state.filters.dateTo) params.set('dateTo', state.filters.dateTo)
    if (state.filters.checkNumber) params.set('checkNumber', state.filters.checkNumber)
    if (state.pagination.page !== 1) params.set('page', state.pagination.page.toString())
    if (state.pagination.pageSize !== 50)
      params.set('pageSize', state.pagination.pageSize.toString())

    setSearchParams(params, { replace: true })
  }, [state, setSearchParams])

  useDebounce(
    () => {
      setDebouncedGroupSearch(groupSearchValue)
    },
    300,
    [groupSearchValue]
  )

  const groupsQuery = useQuery({
    queryKey: ['ca-epay-groups', { search: debouncedGroupSearch }] as const,
    enabled: isInitialized,
    async queryFn({ queryKey, signal }) {
      const [, queryParams] = queryKey
      const params = new URLSearchParams()
      if (queryParams.search) {
        params.set('search', queryParams.search)
      }

      const response = await $api.get<CAEpayGroup[]>(`/ca/epay/groups?${params.toString()}`, {
        signal
      })
      return response.data
    },
    keepPreviousData: false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false
  })

  const checkRegisterQuery = useQuery({
    queryKey: [
      'ca-epay-check-registers',
      {
        groupNumber: state.filters.groupNumber,
        dateFrom: state.filters.dateFrom,
        dateTo: state.filters.dateTo,
        checkNumber: state.filters.checkNumber,
        page: state.pagination.page,
        pageSize: state.pagination.pageSize
      }
    ] as const,
    enabled: isInitialized && !!state.filters.groupNumber,
    async queryFn({ queryKey, signal }) {
      const [, params] = queryKey

      const searchParams = new URLSearchParams()
      if (params.groupNumber) {
        searchParams.set('group-number', params.groupNumber)
      }
      if (params.dateFrom) {
        searchParams.set('date-from', params.dateFrom)
      }
      if (params.dateTo) {
        searchParams.set('date-to', params.dateTo)
      }
      if (params.checkNumber) {
        searchParams.set('check-number', params.checkNumber)
      }
      searchParams.set('page', params.page.toString())
      searchParams.set('page-size', params.pageSize.toString())

      const response = await $api.get<CAEpayCheckRegisterResponse>(
        `/ca/epay/check-registers?${searchParams.toString()}`,
        { signal }
      )
      return response.data
    }
  })

  const handleRefresh = useCallback(() => {
    void checkRegisterQuery.refetch()
  }, [checkRegisterQuery])

  const selectedGroup = useMemo(() => {
    if (!state.filters.groupNumber || !groupsQuery.data) return null
    return groupsQuery.data.find(g => g.number === state.filters.groupNumber) ?? null
  }, [state.filters.groupNumber, groupsQuery.data])

  const columns = useMemo<ColumnsType<CAEpayCheckRegisterRow>>(
    () => [
      {
        title: 'Check #',
        dataIndex: 'checkNumber',
        width: 120,
        onCell: () => ({ className: cx(baseCellClassName, 'font-mono') }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Check Date',
        dataIndex: 'checkDate',
        width: 110,
        render: (value: string | null) =>
          value ? dayjs(value).format('MM/DD/YYYY') : <EmptyCellValue />,
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Group #',
        dataIndex: 'groupNumber',
        width: 100,
        onCell: () => ({ className: cx(baseCellClassName, 'font-mono') }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Employee',
        dataIndex: 'employeeName',
        width: 160,
        ellipsis: true,
        render: (value: string | null, record: CAEpayCheckRegisterRow) =>
          value ? (
            <Tooltip title={record.memberId ? `ID: ${record.memberId}` : undefined}>
              <span>{value}</span>
            </Tooltip>
          ) : (
            <EmptyCellValue />
          ),
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Claimant',
        dataIndex: 'claimantName',
        width: 140,
        ellipsis: true,
        render: (value: string | null) => value ?? <EmptyCellValue />,
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Provider',
        dataIndex: 'providerName',
        width: 160,
        ellipsis: true,
        render: (value: string | null) => value ?? <EmptyCellValue />,
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Claim #',
        dataIndex: 'claimNumber',
        width: 120,
        render: (value: string | null) =>
          value ? <span className='font-mono'>{value}</span> : <EmptyCellValue />,
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Pay To',
        dataIndex: 'payTo',
        width: 90,
        render: (value: string | null) => {
          if (!value) return <EmptyCellValue />
          return (
            <Tag color={value === 'Claimant' ? 'blue' : 'purple'} className='text-[10px]'>
              {value}
            </Tag>
          )
        },
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Amount',
        dataIndex: 'checkAmount',
        width: 120,
        align: 'right',
        render: (value: number) => (
          <span className={cx('font-mono', value < 0 && 'text-red-600')}>
            {currencyFormatter.format(value)}
          </span>
        ),
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 100,
        align: 'center',
        render: (value: CAEpayCheckRegisterRow['status']) => {
          const config: Record<typeof value, { color: string; label: string }> = {
            issued: { color: 'green', label: 'Issued' },
            void: { color: 'red', label: 'Void' },
            adjustment: { color: 'orange', label: 'Adjustment' }
          }
          const { color, label } = config[value] ?? { color: 'default', label: value }
          return <Tag color={color}>{label}</Tag>
        },
        onCell: () => ({ className: baseCellClassName }),
        onHeaderCell: () => ({ className: baseHeaderCellClassName })
      }
    ],
    []
  )

  return (
    <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
      <div className='flex justify-between items-start'>
        <h1 className='text-2xl'>CA ePay Check Registers</h1>
      </div>

      <div className='flex flex-col rounded-lg border bg-white'>
        <Filters
          filters={state.filters}
          groups={groupsQuery.data}
          groupsLoading={groupsQuery.isFetching}
          isRefreshing={checkRegisterQuery.isFetching}
          groupSearchValue={groupSearchValue}
          onGroupSearch={setGroupSearchValue}
          adjustedFromDate={checkRegisterQuery.data?.lastCheckDate ?? null}
          onGroupChange={value => dispatch({ type: 'filters/groupNumber/set', payload: value })}
          onDateFromChange={value => dispatch({ type: 'filters/dateFrom/set', payload: value })}
          onDateToChange={value => dispatch({ type: 'filters/dateTo/set', payload: value })}
          onCheckNumberChange={value =>
            dispatch({ type: 'filters/checkNumber/set', payload: value })
          }
          onRefresh={handleRefresh}
        />

        <div className='bg-gray-50 border-b px-6 py-4 font-mono text-xs leading-relaxed'>
          <div className='text-center font-bold text-sm tracking-[0.3em] mb-2'>
            M O N T H L Y &nbsp; C H E C K &nbsp; R E G I S T E R
          </div>
          <div className='text-center text-gray-600 space-y-0.5'>
            <div>LUCENT HEALTH SOLUTIONS</div>
            <div>Date: {dayjs().format('MM/DD/YYYY')}</div>
            {selectedGroup && (
              <div>
                Group: {selectedGroup.number}
                {selectedGroup.name ? ` - ${selectedGroup.name}` : ''}
              </div>
            )}
            {(state.filters.dateFrom || state.filters.dateTo) && (
              <div>
                {state.filters.dateFrom
                  ? `From: ${dayjs(state.filters.dateFrom).format('MM/DD/YYYY')}`
                  : ''}
                {state.filters.dateTo
                  ? ` Thru: ${dayjs(state.filters.dateTo).format('MM/DD/YYYY')}`
                  : ''}
              </div>
            )}
          </div>
        </div>

        <Table
          size='small'
          rowKey='id'
          columns={columns}
          dataSource={checkRegisterQuery.data?.data ?? []}
          loading={checkRegisterQuery.isFetching}
          className='rounded-none payment-files-table'
          pagination={{
            current: state.pagination.page,
            pageSize: state.pagination.pageSize,
            total: checkRegisterQuery.data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            showTotal: (total, [from, to]) => (
              <span className='text-sm text-gray-500'>
                Showing {from}–{to} of {total} records
              </span>
            )
          }}
          onChange={pagination => {
            if (pagination.current !== state.pagination.page) {
              dispatch({ type: 'pagination/page/set', payload: pagination.current ?? 1 })
            }
            if (pagination.pageSize !== state.pagination.pageSize) {
              dispatch({ type: 'pagination/pageSize/set', payload: pagination.pageSize ?? 50 })
            }
          }}
        />
      </div>
    </div>
  )
}
